import { FC, useEffect, useState } from 'react'
import { useFormikContext } from 'formik'
import { useQuery } from '@tanstack/react-query'
import debounce from 'lodash/debounce'
import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import { ClientOrganization, callSearchClientOrganizations } from '@/api'
import useTranslate from '@/hooks/use-translate'
import DialogUploadValues from '@/types/dialogUploadValues'
import { NfField } from '../primitives/NfField'
import { nfMenuPaperSx } from '../nfFieldSx'

/**
 * Client associé — optionnel, jamais pré-rempli (TEC-1789) : le premier client
 * de la liste n'est pas un défaut pertinent, et un dossier rattaché au mauvais
 * client passe inaperçu.
 */
export const ClientField: FC = () => {
  const { data: clientOrganizationData, isLoading } = useQuery({
    queryKey: ['clientOrganization'],
    queryFn: () => callSearchClientOrganizations({ name: undefined }),
  })

  const formik = useFormikContext<DialogUploadValues>()
  const [clientsOrganization, setClientsOrganization] = useState<
    ClientOrganization[]
  >([])

  const { t: tCommon } = useTranslate('common')
  const { t: tUpload } = useTranslate('upload')

  const handleInputChange = debounce((_event: unknown, newInputValue: string) => {
    void callSearchClientOrganizations({ name: newInputValue }).then(
      (results) => setClientsOrganization(results.data as ClientOrganization[])
    )
  }, 500)

  useEffect(() => {
    setClientsOrganization(clientOrganizationData?.data || [])
  }, [clientOrganizationData])

  const hasError = !!(formik.touched.clientOrganizationId && formik.errors.clientOrganizationId)
  const noClients = !isLoading && (clientOrganizationData?.data?.length ?? 0) === 0

  return (
    <NfField
      label={tCommon('clientOrganization')}
      tooltip={tUpload('wizardClientHint')}
    >
      <Autocomplete
        options={[{ name: '', coid: '' }, ...clientsOrganization]}
        getOptionLabel={(option: ClientOrganization) => option?.name || ''}
        isOptionEqualToValue={(option, value) => option.coid === value.coid}
        getOptionDisabled={(option) => option.name === ''}
        disabled={noClients}
        id="client"
        onInputChange={handleInputChange}
        {...formik.getFieldProps('clientOrganizationId')}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={
              noClients
                ? tUpload('wizardNoClientOption')
                : tUpload('wizardClientSearchPlaceholder')
            }
            error={hasError}
            variant="outlined"
          />
        )}
        value={
          clientsOrganization.find(
            (co) => co.coid === formik.values.clientOrganizationId
          ) || null
        }
        onChange={(_event, newValue) => {
          const value = Array.isArray(newValue) ? newValue[0] : newValue
          formik.setFieldValue('clientOrganizationId', value ? value.coid : '')
        }}
        ListboxProps={{
          sx: {
            '& .MuiAutocomplete-option': { fontSize: 11.5 },
            '& .MuiAutocomplete-groupLabel': { fontSize: 10 },
          },
        }}
        noOptionsText={tUpload('wizardNoClientFound')}
        slotProps={{ paper: { sx: nfMenuPaperSx } }}
        data-testid="customer-autocomplete"
      />
    </NfField>
  )
}

export default ClientField
